import React, { useState } from 'react';
import { Box, CircularProgress, Link as MuiLink, Modal, Typography } from '@mui/material';
import { AuthUIProvider, AuthView } from '@daveyplate/better-auth-ui';
import { Row, Card, Logo } from 'components/lib';
import { UMButton } from '@urbanmetrix/um-kit';
import { authClient } from '../../../lib/auth-client';
import { authUIConfig } from '../../../lib/auth-ui-config';
import { SIGN_UP_EMAIL_KEY } from '../../../app/auth';
import { buildApiUrl } from 'api/apiClient';

const TERMS_OF_USE_URL = 'https://urbanmetrix.com/terms-of-use';
const signUpFields = ['name', 'accept'];
const DUPLICATE_ORGANIZATION_OWNER_MESSAGE =
  'You have already registered another organization account using this email address. The same email address can be associated with only one organization account.';

type SignUpEmailParams = Parameters<typeof authClient.signUp.email>;
type SignUpEmailOptions = SignUpEmailParams[1];

interface SignUpEmailData {
  email: string;
  password: string;
  name: string;
  accept?: boolean;
  callbackURL?: string;
  rememberMe?: boolean;
  [key: string]: unknown;
}

async function isDuplicateExistingAccount(email: string, password: string) {
  const response = await fetch(buildApiUrl('/api/auth/sign-in/email'), {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({
      email,
      password,
      rememberMe: false,
    }),
    credentials: 'omit',
  });

  if (response.ok) {
    return true;
  }

  let errorBody: {
    code?: string;
    error?: {
      code?: string;
    };
  } | null = null;

  try {
    errorBody = await response.json();
  } catch (error) {}

  return (errorBody?.code || errorBody?.error?.code) !== 'EMAIL_NOT_VERIFIED';
}

type SignupProps = Record<string, never>;

export function Signup(_props: SignupProps) {
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [signUpError, setSignUpError] = useState('');

  const signUpAuthClient = Object.assign(Object.create(authClient), {
    signUp: {
      ...authClient.signUp,
      async email(data: SignUpEmailData, options: SignUpEmailOptions) {
        setSignUpError('');

        const { accept, callbackURL, ...payload } = data;
        const response = await authClient.signUp.email(
          {
            ...payload,
            rememberMe: false,
          } as SignUpEmailParams[0],
          options
        );

        if (!response?.token && !response?.data?.token) {
          if (
            payload.email &&
            payload.password &&
            (await isDuplicateExistingAccount(payload.email, payload.password))
          ) {
            setSignUpError(DUPLICATE_ORGANIZATION_OWNER_MESSAGE);
            throw new Error(DUPLICATE_ORGANIZATION_OWNER_MESSAGE);
          }

          if (payload.email) {
            sessionStorage.setItem(SIGN_UP_EMAIL_KEY, payload.email);
          }
        }

        return response;
      },
    },
  });

  function openTermsModal(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    event.stopPropagation();
    setIframeLoading(true);
    setTermsModalOpen(true);
  }

  function closeTermsModal() {
    setTermsModalOpen(false);
  }

  return (
    <Row title="Register to" customClass="auth-form-row">
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '15px',
        }}
      >
        <Logo linked={false} />
      </div>

      <Card restrictWidth center extraClass="form-container" isStat={true}>
        {signUpError && <div className="already-registered signup-inline-error">{signUpError}</div>}
        <AuthUIProvider
          authClient={signUpAuthClient}
          {...authUIConfig}
          signUp={{
            fields: signUpFields,
          }}
          additionalFields={{
            accept: {
              label: (
                <span>
                  Accept{' '}
                  <button className="underline underline-offset-2" type="button" onClick={openTermsModal}>
                    terms &amp; conditions
                  </button>
                </span>
              ),
              errorMessage: {
                required: 'Accept terms & conditions',
              },
              required: true,
              type: 'boolean',
            },
          }}
        >
          <AuthView
            view="SIGN_UP"
            redirectTo="/register/verify"
            className="um-signup-auth max-w-none border-0 bg-transparent !py-3 shadow-none gap-3"
            classNames={{
              base: '!mt-0',
              content: 'pt-0 !mt-0 gap-3',
              header: 'p-0 pb-2',
              title: 'hidden',
              description: 'hidden',
              form: {
                input: 'border-0 bg-white/35',
                error: '!text-red-600',
                primaryButton:
                  'cursor-pointer rounded-full border border-black bg-transparent text-current hover:bg-transparent',
              },
              footerLink: 'cursor-pointer',
            }}
            localization={{
              SIGN_UP: '',
              SIGN_IN: 'Login',
              NAME: 'Full name',
              EMAIL: 'Email',
              PASSWORD: 'Password',
            }}
          />
        </AuthUIProvider>
      </Card>

      <Modal open={termsModalOpen} onClose={closeTermsModal} className="custom-modal">
        <Box className="signup-terms-modal">
          <Box className="signup-terms-modal-header">
            <Typography variant="h6" component="h2"></Typography>
            <UMButton color="white" text="Close" onClick={closeTermsModal} />
          </Box>

          <Box className="signup-terms-iframe-shell">
            {iframeLoading && (
              <Box className="signup-terms-iframe-loader">
                <CircularProgress size={28} />
              </Box>
            )}
            <Box
              component="iframe"
              src={TERMS_OF_USE_URL}
              title="Urbanmetrix Terms of Use"
              onLoad={() => setIframeLoading(false)}
              className="signup-terms-iframe"
            />
          </Box>

          <Typography variant="caption" className="signup-terms-modal-caption">
            If the page does not load here, open it in a new tab:{' '}
            <MuiLink href={TERMS_OF_USE_URL} target="_blank" rel="noreferrer">
              terms of use
            </MuiLink>
            .
          </Typography>
        </Box>
      </Modal>
    </Row>
  );
}
